import React, { PureComponent } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Body,
  Footer,
  FormHeader,
  FormSelect,
  FormTextField,
  Header,
  View,
} from 'components';
import { ids } from 'config';
import { compose } from 'redux';
import {
  Field,
  Form,
  initialize,
  InjectedFormProps,
  reduxForm,
  submit,
} from 'redux-form';
import { fetchCheckInData } from 'store/actions';
import { activeAuthorizations } from 'store/cashiering/authorization/selectors';
import { getReservationOutstandingDeposit } from 'store/cashiering/reservationOutstandingDeposit/selectors';
import {
  getActiveBillingInstructions,
  getAddons,
  getFolios,
  getPreAuthorizationAmount,
} from 'store/cashiering/selectors';
import {
  chooseProfile,
  createIndividual,
  fetchCreateIndividualStatus,
  fetchProfile,
  updateProfileDetails,
} from 'store/profile/actions';
import {
  getIsIndividualCreated,
  getNewIndividualId,
  getProfile,
} from 'store/profile/selectors';
import { getLocalPropertyDateTime } from 'store/propertyManagement/selectors';
import {
  fetchReservationExtended,
  updateReservationProfile,
} from 'store/reservation/actions';
import {
  getReservation,
  getReservationBreakdown,
  getReservationId,
  getReservationVersion,
  isMultiRoomReservation,
} from 'store/reservation/selectors';
import { getErrors } from 'store/selectors';
import { PurchaseItem } from 'types/Api/Availability';
import {
  BillingInstruction,
  CreditCardAuthorizationDetails,
  Folio,
} from 'types/Api/Cashiering';
import { Profile } from 'types/Api/Profile';
import {
  ReservationExtendedBreakdown,
  ReservationView,
} from 'types/Api/Reservation';
import { ApiError, Money } from 'types/Api/Shared';
import Store from 'types/Store';
import { Configurator, Router } from 'utils';
import { getlanguageCode } from 'utils/Configurator';
import { mapReservationExtendedBreakdownToUpdateBreakdownPayload } from 'utils/Shared';
import { required } from 'utils/Validator';
import { configureCheckInRouting } from 'views/CheckInAuth/routing';

import { SelectOption } from '@ac/kiosk-components';

import { DEFAULT_APP_LANGUAGE } from '@gss/configs/constants';
import { getExternalSettings } from '@gss/store/settings/selectors';
import { Grid } from '@material-ui/core';

const { CHECK_IN_FAILED_MESSAGE } = Configurator.getTranslationCodes();

interface CheckInNameDetailsProps
  extends RouteComponentProps,
    InjectedFormProps,
    WithTranslation {
  newIndividualId: string;
  reservation: ReservationView;
  reservationId: string;
  reservationVersion: number;
  reservationBreakdown: ReservationExtendedBreakdown[];
  isIndividualCreated: boolean;
  profile: Profile;
  errors: ApiError[];
  addons: PurchaseItem[];
  activeAuthorizations: CreditCardAuthorizationDetails[];
  outstandingDeposit: Money;
  localPropertyDateTime: string;
  preAuthorizationAmount: number;
  billingInstructions: BillingInstruction[];
  folios: Folio[];
  isMultiRoomReservation: boolean;
  externalSettings: ReturnType<typeof getExternalSettings>;
  updateReservationProfile: typeof updateReservationProfile;
  updateProfileDetails: typeof updateProfileDetails;
  createIndividual: typeof createIndividual;
  fetchProfile: typeof fetchProfile;
  chooseProfile: typeof chooseProfile;
  fetchCreateIndividualStatus: typeof fetchCreateIndividualStatus;
  fetchReservationExtended: typeof fetchReservationExtended;
  submit: typeof submit;
  onInitialize: typeof initialize;
  fetchCheckInData: typeof fetchCheckInData;
}

interface CheckInNameDetailsState {
  error?: ApiError;
  titleOptions: Array<{
    value: string;
    title: string;
  }>;
}

class CheckInNameDetails extends PureComponent<
  CheckInNameDetailsProps,
  CheckInNameDetailsState
> {
  public static defaultProps = {};

  state: CheckInNameDetailsState = {
    titleOptions: [],
    error: undefined,
  };

  componentDidMount() {
    const { isIndividualCreated } = this.props;
    this.prepareTitleOptions();
    isIndividualCreated && this.reinitializeForm();
  }

  componentDidUpdate(prevProps: CheckInNameDetailsProps) {
    const { t } = this.props;
    if (prevProps.t !== t) {
      this.prepareTitleOptions();
    }
  }

  public render() {
    const { t, invalid, submitting, handleSubmit, errors } = this.props;
    const { titleOptions, error } = this.state;
    const isSubmitDisabled = invalid || submitting;

    return (
      <View
        idle={{ type: 'modal' }}
        modal={{
          values: (error && [error]) || errors,
          customErrorCode: CHECK_IN_FAILED_MESSAGE,
          onClick: this.clearLocalErrors,
          shouldClearErrors: true,
        }}
      >
        <Header title={`${t('CHECK_IN')} - ${t('NAME_DETAILS')}`} />
        <Body>
          <FormHeader
            title={t('NAME_DETAILS_TITLE')}
            subtitle={t('NAME_DETAILS_SUBTITLE')}
          />
          <Form onSubmit={handleSubmit(this.onSubmit)} autoComplete="off">
            <Grid container spacing={3}>
              <Grid container item spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="title"
                    component={FormSelect}
                    options={titleOptions}
                    label={t('TITLE_OPTIONAL')}
                    empty
                  />
                </Grid>
              </Grid>
              <Grid container item spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="firstName"
                    component={FormTextField}
                    validate={[required]}
                    label={t('FIRST_NAME')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="lastName"
                    component={FormTextField}
                    validate={[required]}
                    label={t('LAST_NAME')}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Form>
        </Body>
        <Footer
          hasCancelButton
          hasContinueButton
          routeName={t('CHECK_IN')}
          onContinue={this.submit}
          isContinueDisabled={isSubmitDisabled}
        />
      </View>
    );
  }

  private submit = () => {
    const { submit } = this.props;
    submit(ids.CHECKIN_NAME_DETAILS);
  };

  private onSubmit = async (values: any) => {
    const {
      history,
      isIndividualCreated,
      reservationId,
      dirty: isDirty,
      fetchCheckInData,
      fetchProfile,
      chooseProfile,
      fetchReservationExtended,
    } = this.props;

    try {
      if (isDirty) {
        if (isIndividualCreated) {
          await this.updatePreviouslyCreatedProfile(values);
        } else {
          await this.createAndAssignNewProfile(values);

          const { newIndividualId } = this.props;

          await Promise.all([
            fetchProfile(newIndividualId),
            fetchReservationExtended(reservationId),
          ]);

          chooseProfile(newIndividualId);
          await fetchCheckInData();
          this.configureRouting();
        }
      }

      history.push(Router.nextStepURL);
    } catch (error) {
      this.setState({ error });
    }
  };

  private createAndAssignNewProfile = async (values: any) => {
    const {
      reservationId,
      createIndividual,
      updateReservationProfile,
      fetchReservationExtended,
      fetchCreateIndividualStatus,
    } = this.props;

    await fetchReservationExtended(reservationId);

    const newProfileData = {
      details: {
        firstName: values.firstName,
        lastName: values.lastName,
        titleCode: values.title,
      },
      typeCode: Configurator.getProfileTypeCode(
        Configurator.profileRoles.INDIVIDUAL
      ),
    };

    await createIndividual(newProfileData);

    const {
      newIndividualId: profileId,
      reservationId: id,
      reservationVersion: version,
      reservationBreakdown,
      reservation: {
        linkedCompany,
        linkedTravelAgent,
        guestContactProfiles,
        guaranteeTypeId,
      },
    } = this.props;

    await fetchCreateIndividualStatus(profileId);
    await updateReservationProfile(
      { id, version },
      {
        breakdown:
          mapReservationExtendedBreakdownToUpdateBreakdownPayload(
            reservationBreakdown
          ),
        profileId,
        linkedCompany,
        linkedTravelAgent,
        guestContactProfiles,
        guaranteeTypeId,
      }
    );
  };

  private updatePreviouslyCreatedProfile = async (values: any) => {
    const {
      newIndividualId: profileId,
      updateProfileDetails,
      fetchProfile,
    } = this.props;

    await fetchProfile(profileId);
    const {
      profile: { version },
    } = this.props;

    const newProfileDetails = {
      firstName: values.firstName,
      lastName: values.lastName,
      titleId: values.title,
    };

    await updateProfileDetails(profileId, version, newProfileDetails);
  };

  private clearLocalErrors = () => {
    this.setState({ error: undefined });
  };

  private mapTitleOptions = (languageCode: string): SelectOption[] => {
    return Configurator.titles
      .map((title) => ({
        value: title.id,
        title: title.description?.find(
          (item) =>
            item.languageCode?.toUpperCase() === languageCode?.toUpperCase()
        )?.content,
      }))
      .filter(
        (titleOption) => titleOption.value && titleOption.title
      ) as SelectOption[];
  };

  private prepareTitleOptions = () => {
    const { i18n, externalSettings } = this.props;

    const defaultLanguageCode = (
      externalSettings?.LANGUAGE.languageCode || DEFAULT_APP_LANGUAGE
    )?.toUpperCase();
    const languageCode = i18n.language.toUpperCase();

    let titleOptions = this.mapTitleOptions(languageCode);

    if (titleOptions.some((titleOption) => !titleOption.title)) {
      titleOptions = this.mapTitleOptions(defaultLanguageCode);
    }

    if (titleOptions.some((titleOption) => !titleOption.title)) {
      const defaultLanguageCode = getlanguageCode();
      titleOptions = this.mapTitleOptions(defaultLanguageCode);
    }

    if (titleOptions.some((titleOption) => !titleOption.title)) {
      titleOptions = [];
    }

    this.setState({
      titleOptions: titleOptions as Array<{ value: string; title: string }>,
    });
  };

  private reinitializeForm = () => {
    const {
      profile: {
        details: { firstName, lastName, titleCode },
      },
      onInitialize,
    } = this.props;

    const titleId = Configurator.titles.find(
      ({ code }) => code === titleCode
    )?.id;

    onInitialize(ids.CHECKIN_NAME_DETAILS, {
      firstName,
      lastName,
      title: titleId || '',
    });
  };

  private configureRouting() {
    const {
      addons,
      activeAuthorizations,
      outstandingDeposit,
      localPropertyDateTime,
      preAuthorizationAmount,
      billingInstructions,
      folios,
      isMultiRoomReservation,
    } = this.props;

    configureCheckInRouting(
      addons,
      activeAuthorizations,
      outstandingDeposit,
      localPropertyDateTime,
      preAuthorizationAmount,
      billingInstructions,
      folios,
      isMultiRoomReservation
    );
  }
}

const mapStateToProps = (state: Store, props: CheckInNameDetailsProps) => ({
  profile: getProfile(state),
  newIndividualId: getNewIndividualId(state),
  reservation: getReservation(state),
  reservationId: getReservationId(state),
  reservationVersion: getReservationVersion(state),
  reservationBreakdown: getReservationBreakdown(state),
  isIndividualCreated: getIsIndividualCreated(state),
  errors: getErrors(state),
  isMultiRoomReservation: isMultiRoomReservation(state),
  folios: getFolios(state),
  billingInstructions: getActiveBillingInstructions(state),
  preAuthorizationAmount: getPreAuthorizationAmount(state),
  localPropertyDateTime: getLocalPropertyDateTime(state),
  outstandingDeposit: getReservationOutstandingDeposit(state),
  activeAuthorizations: activeAuthorizations(state),
  addons: getAddons(state, props),
  externalSettings: getExternalSettings(state),
});

const mapDispatchToProps = {
  submit,
  createIndividual,
  updateReservationProfile,
  updateProfileDetails,
  fetchCreateIndividualStatus,
  fetchReservationExtended,
  fetchProfile,
  chooseProfile,
  fetchCheckInData,
  onInitialize: initialize,
};

export default compose(
  withRouter,
  withTranslation(),
  reduxForm({ form: ids.CHECKIN_NAME_DETAILS }),
  connect(mapStateToProps, mapDispatchToProps)
)(CheckInNameDetails) as () => JSX.Element;
